import { supabase } from '@/integrations/supabase/client';

export interface Clip {
  id: string;
  created_at: string;
  title: string;
  description: string;
  url: string;
  videoUrl?: string;
  thumbnail_url: string;
  thumbnail?: string;
  video_url?: string;
  download_url?: string;
  duration: number;
  playlist_id: string;
  channel_name?: string;
  views?: number;
  downloads?: number;
  tags?: string[];
}

export interface License {
  id: string;
  rights_holder: string;
  mentions: string[];
  hashtags: string[];
  permissions: any[];
  restrictions: any[];
  clip_language?: string;
  license_validity_date?: string;
  geographic_zone?: string;
  authorized_platforms?: string[];
  max_extract_duration?: number;
  native_monetization_allowed?: boolean;
  ai_editing_tools_allowed?: boolean;
  ai_visual_transformation_allowed?: boolean;
  ai_training_database_allowed?: boolean;
  challenge_participation?: boolean;
}

export const clipsApi = {
  /**
   * Get all clips
   */
  getAllClips: async (): Promise<Clip[]> => {
    try {
      const { data, error } = await supabase
        .from('clips')
        .select('*');

      if (error) {
        console.error('Error fetching all clips:', error);
        return [];
      }

      if (!data || data.length === 0) {
        console.log('No clips found.');
        return [];
      }

      // Transform database fields to match Clip interface
      return data.map(item => ({
        id: item.id,
        created_at: item.created_at,
        title: item.title,
        description: item.description || '',
        url: item.video_url,
        videoUrl: item.video_url,
        thumbnail_url: item.thumbnail,
        thumbnail: item.thumbnail,
        video_url: item.video_url,
        download_url: item.download_url,
        duration: item.duration,
        playlist_id: item.playlist_id,
        views: item.views,
        downloads: item.downloads,
        tags: item.tags || []
      }));
    } catch (error) {
      console.error('Error in getAllClips:', error);
      return [];
    }
  },

  /**
   * Get clip by ID
   * @param clipId The clip ID
   */
  getClipById: async (clipId: string): Promise<Clip | null> => {
    try {
      // Get clip first
      const { data, error } = await supabase
        .from('clips')
        .select('*')
        .eq('id', clipId)
        .single();

      if (error) {
        console.error('Error fetching clip by ID:', error);
        return null;
      }

      if (!data) {
        console.log('Clip not found with ID:', clipId);
        return null;
      }

      // Now get channel name if we have a playlist ID
      let channelName = '';
      if (data.playlist_id) {
        try {
          const { data: playlistData } = await supabase
            .from('playlists')
            .select('channel_id')
            .eq('id', data.playlist_id)
            .single();
            
          if (playlistData?.channel_id) {
            const { data: channelData } = await supabase
              .from('channels')
              .select('name')
              .eq('id', playlistData.channel_id)
              .single();
              
            if (channelData) {
              channelName = channelData.name;
            }
          }
        } catch (e) {
          console.error('Error getting channel name:', e);
        }
      }

      // Transform database fields to match Clip interface
      return {
        id: data.id,
        created_at: data.created_at,
        title: data.title,
        description: data.description || '',
        url: data.video_url,
        videoUrl: data.video_url,
        thumbnail_url: data.thumbnail,
        thumbnail: data.thumbnail,
        video_url: data.video_url,
        download_url: data.download_url,
        duration: data.duration,
        playlist_id: data.playlist_id,
        channel_name: channelName,
        views: data.views,
        downloads: data.downloads,
        tags: data.tags || []
      };
    } catch (error) {
      console.error('Error in getClipById:', error);
      return null;
    }
  },
  
  /**
   * Get clips by playlist ID
   * @param playlistId The playlist ID
   * @param limit Optional limit of clips to return (default: 50)
   */
  getClipsByPlaylistId: async (playlistId: string, limit: number = 50): Promise<Clip[]> => {
    try {
      const { data, error } = await supabase
        .from('clips')
        .select('*')
        .eq('playlist_id', playlistId)
        .limit(limit);

      if (error) {
        console.error('Error fetching clips by playlist ID:', error);
        return [];
      }

      if (!data || data.length === 0) {
        console.log('No clips found for playlist ID:', playlistId);
        return [];
      }

      // Transform database fields to match Clip interface
      return data.map(item => ({
        id: item.id,
        created_at: item.created_at,
        title: item.title,
        description: item.description || '',
        url: item.video_url,
        thumbnail_url: item.thumbnail,
        duration: item.duration,
        playlist_id: item.playlist_id
      }));
    } catch (error) {
      console.error('Error in getClipsByPlaylistId:', error);
      return [];
    }
  },

  /**
   * Create a new clip
   * @param clipData The clip data
   */
  createClip: async (clipData: Omit<Clip, 'id' | 'created_at'>): Promise<Clip | null> => {
    try {
      // Transform Clip interface to database fields
      const dbData = {
        title: clipData.title,
        description: clipData.description,
        video_url: clipData.url,
        thumbnail: clipData.thumbnail_url,
        duration: clipData.duration,
        playlist_id: clipData.playlist_id,
        // Add required fields that weren't in the interface
        download_url: clipData.url, // Using video URL as download URL
        license_id: '00000000-0000-0000-0000-000000000000', // Default license ID
        tags: [] // Empty tags array
      };

      const { data, error } = await supabase
        .from('clips')
        .insert(dbData)
        .select('*')
        .single();

      if (error) {
        console.error('Error creating clip:', error);
        return null;
      }

      if (!data) {
        console.log('Failed to create clip.');
        return null;
      }

      // Transform database fields back to Clip interface
      return {
        id: data.id,
        created_at: data.created_at,
        title: data.title,
        description: data.description || '',
        url: data.video_url,
        thumbnail_url: data.thumbnail,
        duration: data.duration,
        playlist_id: data.playlist_id
      };
    } catch (error) {
      console.error('Error in createClip:', error);
      return null;
    }
  },

  /**
   * Update an existing clip
   * @param clipId The clip ID
   * @param clipData The clip data to update
   */
  updateClip: async (clipId: string, clipData: Partial<Clip>): Promise<Clip | null> => {
    try {
      // Transform Clip interface fields to database fields
      const dbData: any = {};
      if (clipData.title !== undefined) dbData.title = clipData.title;
      if (clipData.description !== undefined) dbData.description = clipData.description;
      if (clipData.url !== undefined) dbData.video_url = clipData.url;
      if (clipData.thumbnail_url !== undefined) dbData.thumbnail = clipData.thumbnail_url;
      if (clipData.duration !== undefined) dbData.duration = clipData.duration;
      if (clipData.playlist_id !== undefined) dbData.playlist_id = clipData.playlist_id;

      const { data, error } = await supabase
        .from('clips')
        .update(dbData)
        .eq('id', clipId)
        .select('*')
        .single();

      if (error) {
        console.error('Error updating clip:', error);
        return null;
      }

      if (!data) {
        console.log('Clip not found with ID:', clipId);
        return null;
      }

      // Transform database fields back to Clip interface
      return {
        id: data.id,
        created_at: data.created_at,
        title: data.title,
        description: data.description || '',
        url: data.video_url,
        thumbnail_url: data.thumbnail,
        duration: data.duration,
        playlist_id: data.playlist_id
      };
    } catch (error) {
      console.error('Error in updateClip:', error);
      return null;
    }
  },

  /**
   * Delete a clip
   * @param clipId The clip ID
   */
  deleteClip: async (clipId: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('clips')
        .delete()
        .eq('id', clipId);

      if (error) {
        console.error('Error deleting clip:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteClip:', error);
      return false;
    }
  },
  
  /**
   * Get license information for a clip
   * @param clipId The clip ID
   */
  getLicenseForClip: async (clipId: string): Promise<License | null> => {
    try {
      console.log("=== LICENSE API DEBUG: Fetching license for clip ID:", clipId);
      
      const { data: clipData, error: clipError } = await supabase
        .from('clips')
        .select('license_id')
        .eq('id', clipId)
        .single();

      if (clipError || !clipData || !clipData.license_id) {
        console.error('=== LICENSE API DEBUG: Error fetching license ID for clip:', clipError || 'No license ID found');
        console.log('=== LICENSE API DEBUG: Clip data:', clipData);
        return null;
      }

      console.log("=== LICENSE API DEBUG: Found license ID:", clipData.license_id);

      // Fetch ALL license fields including the new ones with explicit type casting
      const { data: licenseData, error: licenseError } = await supabase
        .from('licenses')
        .select(`
          id,
          rights_holder,
          mentions,
          hashtags,
          permissions,
          restrictions,
          clip_language,
          license_validity_date,
          geographic_zone,
          authorized_platforms,
          max_extract_duration,
          native_monetization_allowed,
          ai_editing_tools_allowed,
          ai_visual_transformation_allowed,
          ai_training_database_allowed,
          challenge_participation
        `)
        .eq('id', clipData.license_id)
        .single();

      if (licenseError || !licenseData) {
        console.error('=== LICENSE API DEBUG: Error fetching license:', licenseError || 'No license found');
        return null;
      }

      console.log("=== LICENSE API DEBUG: Full license data retrieved:", licenseData);
      console.log("=== LICENSE API DEBUG: Raw boolean values from database:", {
        native_monetization_allowed: licenseData.native_monetization_allowed,
        ai_editing_tools_allowed: licenseData.ai_editing_tools_allowed,
        ai_visual_transformation_allowed: licenseData.ai_visual_transformation_allowed,
        ai_training_database_allowed: licenseData.ai_training_database_allowed,
        challenge_participation: licenseData.challenge_participation,
        types: {
          native_monetization_allowed: typeof licenseData.native_monetization_allowed,
          ai_editing_tools_allowed: typeof licenseData.ai_editing_tools_allowed,
          ai_visual_transformation_allowed: typeof licenseData.ai_visual_transformation_allowed,
          ai_training_database_allowed: typeof licenseData.ai_training_database_allowed,
          challenge_participation: typeof licenseData.challenge_participation
        }
      });

      // Return the license data with proper boolean handling
      const result: License = {
        id: licenseData.id,
        rights_holder: licenseData.rights_holder || '',
        mentions: licenseData.mentions || [],
        hashtags: licenseData.hashtags || [],
        permissions: licenseData.permissions || [],
        restrictions: licenseData.restrictions || [],
        clip_language: licenseData.clip_language,
        license_validity_date: licenseData.license_validity_date,
        geographic_zone: licenseData.geographic_zone,
        authorized_platforms: licenseData.authorized_platforms,
        max_extract_duration: licenseData.max_extract_duration,
        // Preserve the actual boolean values from the database
        native_monetization_allowed: licenseData.native_monetization_allowed,
        ai_editing_tools_allowed: licenseData.ai_editing_tools_allowed,
        ai_visual_transformation_allowed: licenseData.ai_visual_transformation_allowed,
        ai_training_database_allowed: licenseData.ai_training_database_allowed,
        challenge_participation: licenseData.challenge_participation
      };

      console.log("=== LICENSE API DEBUG: Final result object:", result);
      console.log("=== LICENSE API DEBUG: Final boolean values:", {
        native_monetization_allowed: result.native_monetization_allowed,
        ai_editing_tools_allowed: result.ai_editing_tools_allowed,
        ai_visual_transformation_allowed: result.ai_visual_transformation_allowed,
        ai_training_database_allowed: result.ai_training_database_allowed,
        challenge_participation: result.challenge_participation
      });

      return result;
    } catch (error) {
      console.error('=== LICENSE API DEBUG: Error in getLicenseForClip:', error);
      return null;
    }
  },
  
  /**
   * Get related clips for a clip
   * @param clipId The clip ID
   * @param limit Optional limit parameter (default: 6)
   */
  getRelatedClips: async (clipId: string, limit: number = 6): Promise<Clip[]> => {
    try {
      // First get the current clip to find its playlist
      const { data: currentClip, error: clipError } = await supabase
        .from('clips')
        .select('playlist_id')
        .eq('id', clipId)
        .single();
        
      if (clipError || !currentClip) {
        console.error('Error fetching current clip:', clipError);
        return [];
      }
      
      // Get other clips from the same playlist
      const { data, error } = await supabase
        .from('clips')
        .select('*')
        .eq('playlist_id', currentClip.playlist_id)
        .neq('id', clipId) // Exclude the current clip
        .limit(limit);
        
      if (error || !data) {
        console.error('Error fetching related clips:', error);
        return [];
      }
      
      // Transform to Clip interface
      return data.map(item => ({
        id: item.id,
        created_at: item.created_at,
        title: item.title,
        description: item.description || '',
        url: item.video_url,
        videoUrl: item.video_url,
        thumbnail_url: item.thumbnail,
        thumbnail: item.thumbnail,
        video_url: item.video_url,
        download_url: item.download_url,
        duration: item.duration,
        playlist_id: item.playlist_id,
        views: item.views,
        downloads: item.downloads,
        tags: item.tags || []
      }));
    } catch (error) {
      console.error('Error in getRelatedClips:', error);
      return [];
    }
  },
  
  /**
   * Increment view count for a clip
   * @param clipId The clip ID
   */
  incrementClipView: async (clipId: string): Promise<boolean> => {
    try {
      const { error } = await supabase.rpc('increment_clip_views', {
        clip_id: clipId
      });
      
      if (error) {
        console.error('Error incrementing clip view:', error);
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Error in incrementClipView:', error);
      return false;
    }
  },
  
  /**
   * Record a clip download
   * @param clipId The clip ID
   * @param clipTitle The clip title
   * @param userId Optional user ID
   */
  recordClipDownload: async (clipId: string, clipTitle: string, userId?: string): Promise<boolean> => {
    try {
      // 1. Increment the download counter on the clip
      const { error: incrementError } = await supabase.rpc('increment_clip_downloads', {
        clip_id: clipId
      });
      
      if (incrementError) {
        console.error('Error incrementing clip download count:', incrementError);
      }
      
      // 2. Record the download in the downloads table if user ID is provided
      if (userId) {
        const { error: recordError } = await supabase
          .from('downloads')
          .insert({
            clip_id: clipId,
            clip_title: clipTitle,
            user_id: userId
          });
          
        if (recordError) {
          console.error('Error recording clip download:', recordError);
        }
      }
      
      return true;
    } catch (error) {
      console.error('Error in recordClipDownload:', error);
      return false;
    }
  }
};
